import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/store';
import { logger } from '../lib/logger';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

/**
 * HealthStatusBadge — subtle indicator next to the ADMIN nav button.
 *
 *  • Healthy  → nothing visible. Zero visual noise on the happy path.
 *  • Unhealthy → a pulsing red dot with an alert icon appears. Clicking
 *    it opens a small dialog showing which sub-check failed and its
 *    error message.
 *
 * Polls ``/api/admin/health/status`` every 60 seconds (matches the
 * watchdog cadence). Only rendered for users with admin/manager
 * privileges (parent handles that gate).
 */
export function HealthStatusBadge() {
  const [status, setStatus] = useState(null);   // 'pass' | 'fail' | null (loading)
  const [detail, setDetail] = useState(null);   // full latest row
  const [showDialog, setShowDialog] = useState(false);

  const poll = useCallback(async () => {
    try {
      const res = await api.getHealthStatus();
      const latest = res.data?.latest;
      if (latest) {
        setStatus(latest.overall);
        setDetail(latest);
        // If newly-failed, flash a toast so admins passing by the tab
        // notice even without staring at the nav.
        if (latest.overall === 'fail') {
          logger.warn('Health watchdog: FAIL', latest.failures);
        }
      } else {
        setStatus(null);
      }
    } catch (err) {
      // Endpoint might be 403 for non-admins; silently ignore so the
      // component just stays invisible.
      logger.warn('Health status poll failed:', err?.response?.status || err);
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60_000);
    return () => clearInterval(t);
  }, [poll]);

  const runNow = async () => {
    try {
      const res = await api.runHealthCheckNow();
      setStatus(res.data.overall);
      setDetail(res.data);
      if (res.data.overall === 'pass') {
        toast.success('All health checks passed');
      }
    } catch (err) {
      toast.error(`Health run failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  // Nothing to render on the happy path — keeps the nav clean.
  if (status !== 'fail') return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowDialog(true)}
        className="relative flex items-center gap-1 ml-1 px-1.5 py-1 rounded-lg hover:bg-red-50 transition-colors group"
        title="System health alert — click for details"
        data-testid="health-status-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-700 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-700" />
        </span>
        <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-wide text-red-700">Health</span>
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-md" data-testid="health-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> System Health Alert
            </DialogTitle>
            <DialogDescription>
              The background watchdog detected an issue.
              Fixing the underlying problem is the safest path — this
              dialog shows what to investigate.
            </DialogDescription>
          </DialogHeader>

          {detail ? (
            <div className="space-y-3">
              <div className="text-xs text-heda-mute">
                Last checked: {new Date(detail.checked_at).toLocaleString()}
              </div>

              {detail.failures?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">Failures</p>
                  {detail.failures.map((f, i) => (
                    <p key={i} className="text-xs text-red-700 font-mono break-all">• {f}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-xs">
                {['pdf', 'whatsapp', 'db'].map(k => {
                  const c = detail.checks?.[k] || {};
                  return (
                    <div key={k} className={`rounded-lg border p-2 ${c.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <p className="font-semibold uppercase tracking-wide text-[10px]">{k}</p>
                      <p className={c.ok ? 'text-emerald-700' : 'text-red-700'}>{c.ok ? 'OK' : 'FAIL'}</p>
                      <p className="text-[10px] text-heda-mute">{c.ms}ms</p>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={runNow}
                className="w-full text-xs py-2 rounded-lg bg-heda-ink text-white hover:bg-black transition-colors"
                data-testid="health-run-now-btn"
              >
                Re-run checks now
              </button>
            </div>
          ) : (
            <p className="text-sm text-heda-mute">Loading details…</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
